import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Amplify, Auth, I18n } from "aws-amplify";
import { AmplifyAuthenticator, AmplifySignIn, AmplifySignUp } from "@aws-amplify/ui-react";
import { onAuthUIStateChange } from "@aws-amplify/ui-components";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import RecaptchaEnterprise from "react-google-recaptcha-enterprise";
import awsconfig from "./aws-exports";
import "./App.scss";
import AppContent from "components/AppContent";
import Footer from "components/Footer/Footer";
import { verifyRecaptcha } from "services/apiMiddleware";
import {
  areAppStatusesEqual,
  fetchAppStatus,
  getAppStatusPollMs,
  getDefaultAppStatus,
} from "services/appStatus";
import { Dimmer, Loader } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import CustomForgotPassword from "services/customForgotPassword";
import MaintenancePage from "components/MaintenancePage/MaintenancePage";
import UpdateBanner from "components/UpdateBanner/UpdateBanner";

const RECAPTCHA_THRESHOLD = 0.7;
const SCORE_SITE_KEY = "6Ldx6HorAAAAACS96IhlVrdafQ5em9KdFYXQsqaA";
const BOX_SITE_KEY = "6LczWJwrAAAAABSn-ku5HdWXVn1Iygl7ACrLPF0e";
const SHOW_SIGN_UP_HEADER = process.env.REACT_APP_SHOW_SIGN_UP_HEADER !== "false";
const APP_STATUS_POLL_MS = getAppStatusPollMs();

Amplify.configure(awsconfig);

const AuthStateApp = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [authState, setAuthState] = useState("unknown");
  const [appStatus, setAppStatus] = useState(getDefaultAppStatus());
  const [isAppStatusLoading, setIsAppStatusLoading] = useState(true);
  const lastAppStatusRef = useRef(getDefaultAppStatus());
  const hasLoadedAppStatusRef = useRef(false);

  useEffect(() => {
    let alive = true;

    const loadAppStatus = async () => {
      const nextStatus = await fetchAppStatus();

      if (!alive) {
        return;
      }

      if (nextStatus == null) {
        setIsAppStatusLoading(false);
        return;
      }

      const hasLoadedAppStatus = hasLoadedAppStatusRef.current;
      const previousStatus = lastAppStatusRef.current;
      const hasStatusChanged = !areAppStatusesEqual(previousStatus, nextStatus);

      lastAppStatusRef.current = nextStatus;
      hasLoadedAppStatusRef.current = true;
      setIsAppStatusLoading(false);

      if (!hasLoadedAppStatus) {
        setAppStatus(nextStatus);
        return;
      }

      if (!hasStatusChanged) {
        return;
      }

      setAppStatus(nextStatus);
      window.location.reload();
    };

    loadAppStatus();

    const intervalId = window.setInterval(loadAppStatus, APP_STATUS_POLL_MS);

    return () => {
      alive = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (alive) setAuthState("signedin");
      } catch {
        if (alive) setAuthState("signedout");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthUIStateChange((next) => {
      if (!next) return;
      setAuthState(next);
    });
    return unsub;
  }, []);

  useEffect(() => {
    dispatch.activeUser.init();
  }, [dispatch]);

  return (
    <div className="app-container">
      {!appStatus.maintenanceMode && appStatus.bannerEnabled ? (
        <UpdateBanner message={appStatus.bannerMessage} />
      ) : null}
      {appStatus.maintenanceMode ? (
        <MaintenancePage message={appStatus.maintenanceMessage} />
      ) : isAppStatusLoading || authState === "unknown" ? (
        <div className="recaptcha-container">
          <Dimmer active inverted>
            <Loader>{t("loading")}</Loader>
          </Dimmer>
        </div>
      ) : authState === "signedin" ? (
        <AppContent />
      ) : (
        <GoogleReCaptchaProvider
          reCaptchaKey={SCORE_SITE_KEY}
          useEnterprise
          scriptProps={{ async: true, defer: true, appendTo: "head" }}
        >
          <Authentication />
        </GoogleReCaptchaProvider>
      )}
      <Footer />
    </div>
  );
};

export default AuthStateApp;

const Authentication = () => {
  const { t } = useTranslation("common");

  I18n.setLanguage("en");
  I18n.putVocabularies({
    en: {
      "User is disabled.":
        "Your account is currently being activated and will be ready to use shortly. After registering, no further action is required on your end. Our team will complete the setup and notify you once activation is confirmed. As part of our security measures, this process is not instantaneous. Allowing a short time between registration and activation helps us verify details, protect your information and ensure your account is correctly configured. Please note that activations are carried out during regular business hours, so if you register in the evening or on a weekend, your account will be prepared on the next working day. For urgent matters, please contact your Bauer Media Outdoor representative.",
    },
  });

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [gateState, setGateState] = useState("checking");
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Cypress) {
      setGateState("passed");
      return;
    }

    const runCheck = async () => {
      if (!executeRecaptcha) return;
      const token = await executeRecaptcha("LOGIN");
      const { score, error } = await verifyRecaptcha(token, SCORE_SITE_KEY, "LOGIN");
      if (error) {
        setGateState("challenge");
        return;
      }
      setGateState(score >= RECAPTCHA_THRESHOLD ? "passed" : "challenge");
    };
    runCheck();
  }, [executeRecaptcha]);

  const handleAzureSSO = async () => {
    try {
      await Auth.federatedSignIn({ customProvider: "BauerMediaOutdoor" });
    } catch (err) {
      console.error("Azure SSO redirect failed:", err);
    }
  };

  const handleCheckbox = async (token) => {
    const { score, error } = await verifyRecaptcha(token, BOX_SITE_KEY, "LOGIN");
    if (!error && score >= RECAPTCHA_THRESHOLD) {
      setGateState("passed");
    } else {
      checkboxRef.current?.reset?.();
    }
  };

  return (
    <div className="auth-wrap">
      {gateState === "checking" ? (
        <div className="recaptcha-container">
          <Dimmer active inverted>
            <Loader>{t("loading")}</Loader>
          </Dimmer>
        </div>
      ) : gateState === "challenge" ? (
        <div className="recaptcha-container">
          <RecaptchaEnterprise
            ref={checkboxRef}
            sitekey={BOX_SITE_KEY}
            onChange={handleCheckbox}
            action="LOGIN"
          />
        </div>
      ) : (
        <AmplifyAuthenticator usernameAlias="email">
          <AmplifySignIn slot="sign-in" usernameAlias="email">
            <div slot="federated-buttons" className="sso-buttons">
              <amplify-button onClick={handleAzureSSO}>Bauer Media Outdoor (SSO)</amplify-button>
            </div>
          </AmplifySignIn>
          <AmplifySignUp
            slot="sign-up"
            usernameAlias="email"
            headerText={
              SHOW_SIGN_UP_HEADER
                ? "Please note that after creating your account, a Bauer Media Outdoor representative will need to activate it before you can log in."
                : undefined
            }
            formFields={[
              {
                type: "given_name",
                label: "Name",
                inputProps: { required: true, autoComplete: "username" },
              },
              {
                type: "family_name",
                label: "Surname",
                inputProps: { required: true, autoComplete: "username" },
              },
              { type: "email", label: "Email", inputProps: { required: true } },
              { type: "phone_number", label: "Phone Number", inputProps: { required: true } },
              {
                type: "password",
                label: "Password",
                hint:
                  "Password must be at least 14 characters long and contain at least 1 uppercase letter (A-Z), 1 lowercase letter (a-z), 1 number (0-9), and 1 special character (for example: ! @ # $ % ^ & *).",
                inputProps: { required: true },
              },
            ]}
          />
          <div slot="forgot-password">
            <CustomForgotPassword />
          </div>
        </AmplifyAuthenticator>
      )}
    </div>
  );
};
